import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { BookOpen, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * BottomTabBar — mobile-only sticky primary nav (§9 of the bottom-nav brief).
 *
 * • 4 tabs (Beranda · Direktori · Diskusi · Jurnal), outline icons, short IDN labels.
 * • Active tab = coral icon + navy label; inactive = --meta gray.
 * • Hidden on desktop (md+) — desktop keeps its existing top nav.
 * • Hidden on focused/transactional flows only: auth (login/register), onboarding,
 *   admin dashboard, and review-success. Every other route — including school
 *   detail (`/sekolah/:id`) — keeps the global bottom nav visible (Feb 2026 spec).
 * • Honors `env(safe-area-inset-bottom)` so it clears iOS home indicators.
 * • Diskusi tab routes to `/forum` — the dua-tab feed (Ruang Anda | Semua Ruang).
 */

// Paths where the tab bar MUST be hidden. Match by prefix.
const HIDE_ON_PATHS = [
  "/onboarding",
  "/login",
  "/register",
  "/admin",
  "/review-success",
];

function shouldHide(pathname) {
  if (HIDE_ON_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"))) return true;
  return false;
}

export default function BottomTabBar() {
  const { pathname } = useLocation();
  const { user } = useAuth();

  if (shouldHide(pathname)) return null;

  // Editorial pivot: The Reports (homepage) + Akun only.
  const tabs = [
    { to: "/", label: "The Reports", icon: BookOpen, match: (p) => p === "/" || p.startsWith("/jurnal"), testid: "tab-jurnal" },
    { to: user ? "/profile" : "/login", label: user ? "Akun" : "Masuk", icon: User, match: (p) => p.startsWith("/profile"), testid: "tab-akun" },
  ];

  const trackClick = (destination) => {
    // Lightweight tracking hook (§2 metric). Gracefully no-ops when no analytics wired.
    try {
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({ event: "tab_nav_click", destination });
      }
    } catch { /* ignore */ }
  };

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white border-t border-[#ECE9E3] shadow-[0_-4px_14px_rgba(20,41,77,0.06)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="bottom-tab-bar"
      aria-label="Navigasi utama"
    >
      <ul className="flex items-stretch">
        {tabs.map((t) => {
          const active = t.match(pathname);
          const Icon = t.icon;
          return (
            <li key={t.label} className="flex-1">
              <NavLink
                to={t.to}
                data-testid={t.testid}
                onClick={() => trackClick(t.label)}
                aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-1 min-h-[56px] py-2 active:opacity-70 active:scale-[0.97] transition-transform ${
                  active ? "" : ""
                }`}
              >
                <Icon
                  className={active ? "w-6 h-6 text-[#C15C3F]" : "w-6 h-6 text-[#8B8B90]"}
                  strokeWidth={active ? 2 : 1.75}
                />
                <span
                  className={`text-[10.5px] leading-none font-sans font-semibold tracking-tight ${
                    active ? "text-[#14294D]" : "text-[#8B8B90]"
                  }`}
                >
                  {t.label}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export { shouldHide as _shouldHideBottomTabBar };
